import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';

import { useA11ySelector } from '@sg/a11y-helpers';
import {
  theme,
  useResponsive,
  useSafeNavigation,
  useSafeRouteParams,
  useSafeRouter,
} from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import {
  AccountScreenBorderedSection,
  AccountScreenContainer,
  AccountScreenSection,
  AccountScreenSectionTitle,
  AccountScreenTitle,
  CreditSection,
  SignedOutView,
} from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import {
  useCustomerCredit,
  usePromoCode,
  usePromoCodeDeepLink,
  useStipendCredits,
} from '../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const CreditAndPromoCodesScreen = () => {
  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const { formatMessage } = useIntl();
  const { match, minWidth } = useResponsive();

  const containerA11ySelector = useA11ySelector('account.credit-and-promo.container'); // prettier-ignore

  const routeParams = useSafeRouteParams<{ promoCode?: string }>();

  usePromoCodeDeepLink(routeParams.promoCode);

  const { credits, isLoadingCredits, refetchCredits, availableCredit } = useCustomerCredit({
    requestPolicy: 'cache-and-network',
    shouldRefetchOnFocus: true,
  });

  // NOTE: We only use stipend credits.
  const { filteredCredits, totalCreditAmount } = useStipendCredits({
    credits,
    availableCredit,
  });

  const isLoggedIn = useIsLoggedIn();

  // ─── Promo Code ──────────────────────────────────────────────────────

  const {
    promoCode,
    promoCodeInvalid,
    isSubmittingPromoCode,
    isPromoCodeApplied,
    setPromoCode,
    submitPromoCode,
  } = usePromoCode(refetchCredits);

  // ─── Callbacks ───────────────────────────────────────────────────────

  const handlePressAvailableCredit = useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.navigate('/credit');
      return;
    }

    navigation?.navigate('Modal', { screen: 'CreditDetails' });
  }, [isExpoRouterEnabled, router, navigation]);

  const handlePressAddPromoCode = useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.navigate('/credit/add');
      return;
    }

    navigation?.navigate('Modal', { screen: 'CreditAdd' });
  }, [isExpoRouterEnabled, router, navigation]);

  // ─────────────────────────────────────────────────────────────────────

  const title = formatMessage(messages.accountCreditAndPromoCodesTitle);

  // ─────────────────────────────────────────────────────────────────────

  if (!isLoggedIn) {
    return <SignedOutView redirectTo="credit-and-promo-codes" withoutFooter />;
  }

  return (
    <AccountScreenContainer screen="credit-and-promo-codes" {...containerA11ySelector.props}>
      <AccountScreenTitle visibility="COMPACT" title={title} />
      {minWidth.isSM ? <AccountScreenSectionTitle title={title} /> : null}

      <AccountScreenSection>
        <AccountScreenBorderedSection style={match([undefined, styles.sectionContainerSM])}>
          <CreditSection
            creditAmount={totalCreditAmount}
            // @ts-expect-error TS(2322): Type 'readonly { readonly __typename?: "Credit" | ... Remove this comment to see the full error message
            creditDetails={filteredCredits}
            promoCode={promoCode}
            isLoadingCredits={isLoadingCredits}
            isSubmittingPromoCode={isSubmittingPromoCode}
            isPromoCodeApplied={isPromoCodeApplied}
            hasPromoCodeError={promoCodeInvalid}
            onPressAvailableCredit={handlePressAvailableCredit}
            onPressAddPromoCode={handlePressAddPromoCode}
            setPromoCode={setPromoCode}
            onPressApplyPromoCode={submitPromoCode}
            isCollapsible={false}
          />
        </AccountScreenBorderedSection>
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accountCreditAndPromoCodesTitle: {
    defaultMessage: 'Credit & promo codes',
    description: 'Account | Credit And Promo Codes | Title',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  sectionContainerSM: {
    maxWidth: 600,
    paddingVertical: theme.spacing['6'],
  },
});
